// ============================================================================
// SAMPLE TAX SERVICE (Reference Implementation)
// ============================================================================
// Business logic and API calls for Tax Configuration module

import { get, post, put, del } from '../client/axiosClient';
import { sampleTaxEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  SampleTax,
  CreateSampleTaxRequest,
  UpdateSampleTaxRequest,
  SampleTaxListItem,
  SampleTaxFilterParams,
  SampleTaxCalculationRequest,
  SampleTaxCalculationResponse,
  SampleTaxReport,
} from '../types';

/**
 * Tax Service Class
 */
class SampleTaxService {
  /**
   * Get all taxes
   */
  async getAll(params?: SampleTaxFilterParams): Promise<ListResponse<SampleTaxListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleTaxEndpoints.list(), params)
      : sampleTaxEndpoints.list();

    const response = await get<ListResponse<SampleTaxListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get tax by ID
   */
  async getById(id: ID): Promise<ApiResponse<SampleTax>> {
    const response = await get<ApiResponse<SampleTax>>(sampleTaxEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new tax
   */
  async create(data: CreateSampleTaxRequest): Promise<ApiResponse<SampleTax>> {
    const response = await post<ApiResponse<SampleTax>>(sampleTaxEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update tax
   */
  async update(id: ID, data: UpdateSampleTaxRequest): Promise<ApiResponse<SampleTax>> {
    const response = await put<ApiResponse<SampleTax>>(sampleTaxEndpoints.update(id), data);
    return response.data;
  }

  /**
   * Delete tax
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleTaxEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search taxes
   */
  async search(params: SampleTaxFilterParams): Promise<ListResponse<SampleTaxListItem>> {
    const response = await post<ListResponse<SampleTaxListItem>>(
      sampleTaxEndpoints.search(),
      params
    );
    return response.data;
  }

  /**
   * Get taxes by jurisdiction
   */
  async getByJurisdiction(jurisdiction: string): Promise<ListResponse<SampleTaxListItem>> {
    const response = await get<ListResponse<SampleTaxListItem>>(
      sampleTaxEndpoints.byJurisdiction(jurisdiction)
    );
    return response.data;
  }

  /**
   * Get taxes by country
   */
  async getByCountry(country: string): Promise<ListResponse<SampleTaxListItem>> {
    const response = await get<ListResponse<SampleTaxListItem>>(
      sampleTaxEndpoints.byCountry(country)
    );
    return response.data;
  }

  /**
   * Get taxes effective on date
   */
  async getEffectiveOn(date: string): Promise<ListResponse<SampleTaxListItem>> {
    const response = await get<ListResponse<SampleTaxListItem>>(
      sampleTaxEndpoints.effectiveOn(date)
    );
    return response.data;
  }

  /**
   * Calculate tax
   */
  async calculate(request: SampleTaxCalculationRequest): Promise<ApiResponse<SampleTaxCalculationResponse>> {
    const response = await post<ApiResponse<SampleTaxCalculationResponse>>(
      sampleTaxEndpoints.calculate(),
      request
    );
    return response.data;
  }

  /**
   * Get active taxes
   */
  async getActive(): Promise<ListResponse<SampleTaxListItem>> {
    const response = await get<ListResponse<SampleTaxListItem>>(sampleTaxEndpoints.active());
    return response.data;
  }

  /**
   * Get tax report
   */
  async getReport(params: any): Promise<ApiResponse<SampleTaxReport>> {
    const response = await post<ApiResponse<SampleTaxReport>>(
      sampleTaxEndpoints.report(),
      params
    );
    return response.data;
  }

  /**
   * Bulk delete taxes
   */
  async bulkDelete(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(sampleTaxEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }

  /**
   * Export taxes
   */
  async export(filters?: SampleTaxFilterParams): Promise<Blob> {
    const endpoint = filters
      ? buildEndpointWithQuery(sampleTaxEndpoints.export(), filters)
      : sampleTaxEndpoints.export();

    const response = await get<Blob>(endpoint, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import taxes
   */
  async import(file: File): Promise<BulkOperationResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await post<BulkOperationResponse>(
      sampleTaxEndpoints.import(),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Get taxes for dropdown/select
   */
  async getForDropdown(): Promise<SampleTaxListItem[]> {
    const response = await this.getActive();
    return response.data;
  }

  /**
   * Get current taxes (effective today)
   */
  async getCurrentTaxes(): Promise<ListResponse<SampleTaxListItem>> {
    const today = new Date().toISOString().split('T')[0];
    return this.getEffectiveOn(today);
  }
}

/**
 * Export singleton instance
 */
export const sampleTaxService = new SampleTaxService();
export default sampleTaxService;
